import React from 'react'
import { AsyncContentProgress, Button, Input, Label, Switch } from '@/shared/ui'
import LoadingCandles from '@/shared/ui/LoadingCandles'
import { extractCommunityMessagePlainText } from '@/modules/comunidade/utils/communityMessageContent'
import { PostTimelineLinkCard } from '@/modules/post-shared'
import { useTimelineRootVisionsForLink } from '../hooks/useTimelineRootVisionsForLink'

type VisionTimelineLinkFieldProps = {
  enabled: boolean
  selectedRootId: number | null
  onEnabledChange: (enabled: boolean) => void
  onSelectedRootIdChange: (id: number | null) => void
  disabled?: boolean
  excludeVisionId?: number | null
}

export function VisionTimelineLinkField({
  enabled,
  selectedRootId,
  onEnabledChange,
  onSelectedRootIdChange,
  disabled,
  excludeVisionId,
}: VisionTimelineLinkFieldProps) {
  const { items, selected, loading, error } = useTimelineRootVisionsForLink(
    enabled,
    selectedRootId,
    excludeVisionId ?? null,
  )
  const [query, setQuery] = React.useState('')
  const [pickerOpen, setPickerOpen] = React.useState(() => enabled && selectedRootId == null)

  React.useEffect(() => {
    if (!enabled) {
      setPickerOpen(false)
      setQuery('')
      return
    }
    if (selectedRootId == null) {
      setPickerOpen(true)
    }
  }, [enabled, selectedRootId])

  const filtered = React.useMemo(() => {
    const q = String(query || '').trim().toLowerCase()
    if (!q) return items
    return (items || []).filter((it) => {
      const title = String(it.title || '').toLowerCase()
      const author = String(it.authorName || '').toLowerCase()
      const content = extractCommunityMessagePlainText(it.contentHtml || '').toLowerCase()
      return title.includes(q) || author.includes(q) || content.includes(q)
    })
  }, [items, query])

  const showPicker = enabled && (pickerOpen || selectedRootId == null)
  const showSelected = enabled && selectedRootId != null && !showPicker

  function handleSelectRoot(id: number) {
    onSelectedRootIdChange(id)
    setPickerOpen(false)
  }

  function handleClearSelection() {
    onSelectedRootIdChange(null)
    setQuery('')
    setPickerOpen(true)
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 space-y-0.5">
          <Label>Vincular post publicado (timeline)</Label>
          <p className="text-xs text-foreground/60">
            Opcional. Unifica a conversa com outro post e cria historico na timeline.
          </p>
        </div>
        <Switch
          checked={enabled}
          disabled={disabled}
          onChange={(e) => {
            const next = (e.target as HTMLInputElement).checked
            onEnabledChange(next)
            if (!next) onSelectedRootIdChange(null)
          }}
        />
      </div>

      {enabled && (
        <div className="relative">
          <div
            className={[
              'transition-all duration-300 ease-out',
              showPicker
                ? 'max-h-[620px] opacity-100 translate-y-0'
                : 'max-h-0 opacity-0 -translate-y-2 overflow-hidden pointer-events-none',
            ].join(' ')}
          >
            <div className="rounded-lg border border-border/40 bg-background/20 p-3">
              <div className="flex items-center gap-2">
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar post publicado (titulo, autor ou conteudo...)"
                  disabled={disabled}
                />
              </div>

              {error ? <div className="mt-2 text-xs text-red-400">{error}</div> : null}

              {loading ? (
                <div className="mt-3 flex w-full items-center justify-center py-2">
                  <LoadingCandles className="min-h-[60px]" label="Carregando posts publicados..." />
                </div>
              ) : filtered.length === 0 ? (
                <div className="mt-3 text-sm text-foreground/70">
                  Nenhum post publicado encontrado.
                </div>
              ) : (
                <div className="mt-3 max-h-[420px] space-y-3 overflow-y-auto pr-1">
                  {filtered.map((it) => (
                    <PostTimelineLinkCard
                      key={it.id}
                      title={it.title}
                      contentHtml={it.contentHtml}
                      authorName={it.authorName}
                      authorAvatarUrl={it.authorAvatarUrl}
                      createdAt={it.createdAt}
                      highlight={selectedRootId != null && Number(it.id) === Number(selectedRootId)}
                      onClick={() => handleSelectRoot(Math.floor(Number(it.id)))}
                      interactive={!disabled}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div
            className={[
              'transition-all duration-300 ease-out',
              showSelected
                ? 'max-h-[260px] opacity-100 translate-y-0'
                : 'max-h-0 opacity-0 translate-y-2 overflow-hidden pointer-events-none',
            ].join(' ')}
          >
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
              <div className="min-w-0 flex-1">
                {selected ? (
                  <PostTimelineLinkCard
                    title={selected.title}
                    contentHtml={selected.contentHtml}
                    authorName={selected.authorName}
                    authorAvatarUrl={selected.authorAvatarUrl}
                    createdAt={selected.createdAt}
                    interactive={false}
                  />
                ) : (
                  <div className="rounded-xl bg-background/20 p-3">
                    {loading ? (
                      <AsyncContentProgress loading label="Carregando post...">
                        {null}
                      </AsyncContentProgress>
                    ) : (
                      <div className="text-xs text-foreground/70">
                        {`Post selecionado: #${selectedRootId}.`}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex w-full flex-col gap-2 sm:w-auto">
                <Button
                  type="button"
                  variant="outline"
                  className="h-9 w-full sm:w-auto"
                  onClick={() => setPickerOpen(true)}
                  disabled={disabled}
                >
                  Alterar
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-9 w-full text-destructive sm:w-auto"
                  onClick={handleClearSelection}
                  disabled={disabled}
                >
                  Excluir
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
